import apiClient from './api.service';
import { Integration, StoreIntegration } from '../types/integration.types';

/**
 * Integration Service
 * Handles all API calls related to integrations
 */
class IntegrationService {
  /**
   * Get all available integrations
   */
  async getIntegrations(): Promise<Integration[]> {
    const response = await apiClient.get<{ success: boolean; data: { integrations: Integration[] } }>(
      '/integrations'
    );
    return response.data.data.integrations;
  }

  /**
   * Get integration by ID
   */
  async getIntegrationById(id: number): Promise<Integration> {
    const response = await apiClient.get<{ success: boolean; data: { integration: Integration } }>(
      `/integrations/${id}`
    );
    return response.data.data.integration;
  }

  /**
   * Get integration by key
   */
  async getIntegrationByKey(key: string): Promise<Integration> {
    const response = await apiClient.get<{ success: boolean; data: { integration: Integration } }>(
      `/integrations/key/${key}`
    );
    return response.data.data.integration;
  }

  /**
   * Get all integrations for a store
   */
  async getStoreIntegrations(storeId: number): Promise<StoreIntegration[]> {
    const response = await apiClient.get<{ success: boolean; data: { integrations: StoreIntegration[] } }>(
      `/integrations/store/${storeId}`
    );
    return response.data.data.integrations;
  }

  /**
   * Initiate OAuth flow
   */
  async initiateOAuth(storeId: number, integrationKey: string): Promise<{ auth_url: string; state: string }> {
    const response = await apiClient.post<{ success: boolean; data: { auth_url: string; state: string } }>(
      `/integrations/store/${storeId}/${integrationKey}/authorize`,
      { returnOrigin: window.location.origin }
    );
    return response.data.data;
  }

  /**
   * Complete OAuth flow (exchange code for tokens)
   */
  async completeOAuth(
    storeId: number,
    integrationKey: string,
    code: string,
    state: string
  ): Promise<{ integration: StoreIntegration; message: string }> {
    const response = await apiClient.post<{ success: boolean; data: { integration: StoreIntegration; message: string } }>(
      `/integrations/store/${storeId}/${integrationKey}/complete`,
      { code, state }
    );
    return response.data.data;
  }

  /**
   * Revoke/deactivate integration
   */
  async revokeIntegration(storeId: number, integrationKey: string, purge = false): Promise<void> {
    const qs = purge ? '?purge=1' : '';
    await apiClient.delete(`/integrations/store/${storeId}/${integrationKey}${qs}`);
  }

  async purgeConversations(storeId: number, integrationKey: string): Promise<void> {
    await apiClient.delete(`/integrations/store/${storeId}/${integrationKey}/conversations`);
  }

  /**
   * Get store information from integration
   */
  async getStoreInfo(storeId: number, integrationKey: string): Promise<any> {
    const response = await apiClient.get<{ success: boolean; data: any }>(
      `/integrations/store/${storeId}/${integrationKey}/store-info`
    );
    return response.data;
  }

  /**
   * Get products from integration (without syncing)
   */
  async getProducts(storeId: number, integrationKey: string, page = 1, limit = 50): Promise<any> {
    const response = await apiClient.get<{ success: boolean; data: any }>(
      `/integrations/store/${storeId}/${integrationKey}/products?page=${page}&limit=${limit}`
    );
    return response.data;
  }

  /**
   * Get orders from integration (without syncing)
   */
  async getOrders(storeId: number, integrationKey: string, page = 1, limit = 50): Promise<any> {
    const response = await apiClient.get<{ success: boolean; data: any }>(
      `/integrations/store/${storeId}/${integrationKey}/orders?page=${page}&limit=${limit}`
    );
    return response.data;
  }

  /**
   * Get checkout fields from integration (dynamically extracted from orders)
   */
  async getCheckoutFields(storeId: number, integrationKey: string): Promise<any> {
    const response = await apiClient.get<{ success: boolean; data: { fields: any[] } }>(
      `/integrations/store/${storeId}/${integrationKey}/checkout-fields`
    );
    return response.data.data.fields;
  }

  /**
   * Get order mapping configuration
   */
  async getOrderMapping(storeId: number, integrationKey: string): Promise<any> {
    const response = await apiClient.get<{ success: boolean; data: any }>(
      `/integrations/store/${storeId}/${integrationKey}/mapping`
    );
    return response.data.data;
  }

  /**
   * Save order mapping configuration
   */
  async saveOrderMapping(
    storeId: number,
    integrationKey: string,
    mappings: any,
    syncConfig?: any
  ): Promise<any> {
    const response = await apiClient.post<{ success: boolean; data: any; message: string }>(
      `/integrations/store/${storeId}/${integrationKey}/mapping`,
      { mappings, sync_config: syncConfig }
    );
    return response.data;
  }

  /**
   * Validate order mapping configuration
   */
  async validateOrderMapping(storeId: number, integrationKey: string, mappings: any): Promise<any> {
    const response = await apiClient.post<{ success: boolean; valid: boolean; errors: string[] }>(
      `/integrations/store/${storeId}/${integrationKey}/mapping/validate`,
      { mappings }
    );
    return response.data;
  }

  /**
   * Preview order mapping transformation
   */
  async previewOrderMapping(
    storeId: number,
    integrationKey: string,
    mappings?: any,
    exampleOrder?: any
  ): Promise<any> {
    const response = await apiClient.post<{ success: boolean; preview: any; error?: string; example_order?: any }>(
      `/integrations/store/${storeId}/${integrationKey}/mapping/preview`,
      { mappings, example_order: exampleOrder }
    );
    return response.data;
  }

  /**
   * Get default order mappings
   */
  async getDefaultOrderMappings(storeId: number, integrationKey: string): Promise<any> {
    const response = await apiClient.get<{ success: boolean; data: any }>(
      `/integrations/store/${storeId}/${integrationKey}/mapping/defaults`
    );
    return response.data.data;
  }

  /**
   * Sync products from the connected store
   */
  async syncProducts(storeId: number, integrationKey: string): Promise<any> {
    const response = await apiClient.post<{ success: boolean; data: any; message: string }>(
      `/integrations/store/${storeId}/${integrationKey}/sync/products`,
      {},
      { timeout: 300000 }
    );
    return response.data;
  }

  async getSyncProgress(
    storeId: number,
    integrationKey: string,
    type: 'products' | 'orders'
  ): Promise<{ running: boolean; label: string; current: number; total: number }> {
    const response = await apiClient.get<{ success: boolean; data: { running: boolean; label: string; current: number; total: number } }>(
      `/integrations/store/${storeId}/${integrationKey}/sync/progress`,
      { params: { type } }
    );
    return response.data.data;
  }

  /**
   * Sync orders with options
   */
  async syncOrders(
    storeId: number,
    integrationKey: string,
    options?: {
      date_from?: string;
      date_to?: string;
      update_existing?: boolean;
      check_duplicates?: boolean;
    }
  ): Promise<any> {
    const response = await apiClient.post<{ success: boolean; data: any; message: string }>(
      `/integrations/store/${storeId}/${integrationKey}/sync/orders`,
      options || {},
      { timeout: 300000 }
    );
    return response.data;
  }

  /**
   * Enable or disable automatic synchronization via webhooks
   */
  async setAutoSync(storeId: number, integrationKey: string, enabled: boolean): Promise<any> {
    const response = await apiClient.put<{ success: boolean; data: any; message: string }>(
      `/integrations/store/${storeId}/${integrationKey}/auto-sync`,
      { enabled }
    );
    return response.data;
  }

  /**
   * Get synchronization logs for a store integration
   */
  async getSyncLogs(
    storeId: number,
    integrationKey: string,
    options?: { limit?: number; offset?: number; sync_type?: string }
  ): Promise<{ logs: any[]; pagination: any }> {
    const params = new URLSearchParams();
    if (options?.limit) params.append('limit', String(options.limit));
    if (options?.offset) params.append('offset', String(options.offset));
    if (options?.sync_type) params.append('sync_type', options.sync_type);

    const response = await apiClient.get<{ success: boolean; data: { logs: any[]; pagination: any } }>(
      `/integrations/store/${storeId}/${integrationKey}/sync-logs?${params.toString()}`
    );
    return response.data.data;
  }
}

export default new IntegrationService();

